'use client';

import {
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from '@/components/ui/command';
import type { FrecencyEntry, FrecencyEntryType } from '@/hooks/use-frecency';
import {
  ArrowRight,
  Building2,
  File,
  Folder,
  Layers,
  MessageSquare,
  Palette,
  User,
  Users,
  Zap,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { ItemIcon, ShortcutHint } from '../primitives';
import {
  AestheticRows,
  AvatarRows,
  CommandRows,
  MemberRows,
  ProductRows,
  ProjectRows,
  ThreadRows,
} from '../sections';
import type {
  AestheticResult,
  AvatarResult,
  CommandDef,
  GoToItem,
  MemberResult,
  OrgResult,
  OrgThreadResult,
  ProductResult,
  ProjectResult,
  SearchScope,
} from '../types';

export type RootPageText = {
  recent: string;
  browse: string;
  recentProducts: string;
  recentThreads: string;
  goTo: string;
  products: string;
  threads: string;
  projects: string;
  avatars: string;
  aesthetics: string;
  members: string;
  quickActions: string;
  organizations: string;
};

const RECENT_ICONS: Record<
  FrecencyEntryType,
  ComponentType<{ className?: string }>
> = {
  product: File,
  thread: MessageSquare,
  project: Folder,
  collection: Layers,
  avatar: User,
  aesthetic: Palette,
  member: User,
  workspace: Building2,
  navigation: ArrowRight,
  action: Zap,
};

export function RootPage({
  text,
  shouldSearch,
  recentEntries,
  products,
  threads,
  orgs,
  projects,
  avatars,
  aesthetics,
  members,
  gotoItems,
  canSearchWorkspaceScopes,
  roleLabels,
  quickActions,
  formatTime,
  onNavigate,
  onSelectRecent,
  onSelectProduct,
  onSelectThread,
  onSelectOrg,
  onSelectProject,
  onSelectAvatar,
  onSelectAesthetic,
  onSelectMember,
  onPushScope,
  onRunCommand,
}: {
  text: RootPageText;
  shouldSearch: boolean;
  recentEntries: FrecencyEntry[];
  products: ProductResult[];
  threads: OrgThreadResult[];
  orgs: OrgResult[];
  projects: ProjectResult[];
  avatars: AvatarResult[];
  aesthetics: AestheticResult[];
  members: MemberResult[];
  gotoItems: GoToItem[];
  canSearchWorkspaceScopes: boolean;
  roleLabels: Record<'owner' | 'admin' | 'member', string>;
  quickActions: CommandDef[];
  formatTime: (timestamp: number) => string;
  onNavigate: (href: string, label?: string) => void;
  onSelectRecent: (entry: FrecencyEntry) => void;
  onSelectProduct: (product: ProductResult) => void;
  onSelectThread: (thread: OrgThreadResult) => void;
  onSelectOrg: (org: OrgResult) => void;
  onSelectProject: (project: ProjectResult) => void;
  onSelectAvatar: (avatar: AvatarResult) => void;
  onSelectAesthetic: (aesthetic: AestheticResult) => void;
  onSelectMember: (member: MemberResult) => void;
  onPushScope: (scope: SearchScope) => void;
  onRunCommand: (command: CommandDef) => void;
}) {
  const showBrowse =
    !shouldSearch &&
    (products.length > 0 || threads.length > 0 || canSearchWorkspaceScopes);

  const workspaceScopes: Array<{
    scope: SearchScope;
    label: string;
    icon: ComponentType<{ className?: string }>;
  }> = [
    { scope: 'projects', label: text.projects, icon: Folder },
    { scope: 'avatars', label: text.avatars, icon: User },
    { scope: 'aesthetics', label: text.aesthetics, icon: Palette },
    { scope: 'members', label: text.members, icon: Users },
  ];

  return (
    <>
      {recentEntries.length > 0 && (
        <CommandGroup heading={text.recent}>
          {recentEntries.map((entry) => (
            <CommandItem
              key={entry.key}
              value={`recent:${entry.key}`}
              onSelect={() => onSelectRecent(entry)}
            >
              <ItemIcon icon={RECENT_ICONS[entry.meta.type] ?? ArrowRight} />
              <span className="min-w-0 flex-1 truncate">
                {entry.meta.label}
              </span>
              <span className="text-muted-foreground/40 text-xs">
                {formatTime(entry.lastUsedAt)}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {showBrowse && (
        <CommandGroup heading={text.browse}>
          {products.length > 0 && (
            <CommandItem
              value="browse:products"
              onSelect={() => onPushScope('products')}
            >
              <ItemIcon icon={File} />
              <span>{text.recentProducts}</span>
              <CommandShortcut>#</CommandShortcut>
            </CommandItem>
          )}
          {threads.length > 0 && (
            <CommandItem
              value="browse:threads"
              onSelect={() => onPushScope('threads')}
            >
              <ItemIcon icon={MessageSquare} />
              <span>{text.recentThreads}</span>
              <CommandShortcut>@</CommandShortcut>
            </CommandItem>
          )}
          {canSearchWorkspaceScopes &&
            workspaceScopes.map((item) => (
              <CommandItem
                key={item.scope}
                value={`browse:${item.scope}`}
                onSelect={() => onPushScope(item.scope)}
              >
                <ItemIcon icon={item.icon} />
                <span>{item.label}</span>
              </CommandItem>
            ))}
        </CommandGroup>
      )}

      {gotoItems.length > 0 && (
        <CommandGroup heading={text.goTo}>
          {gotoItems.map((item) => (
            <CommandItem
              key={item.id}
              value={`goto:${item.id}`}
              onSelect={() => onNavigate(item.href, item.label)}
            >
              <ItemIcon icon={item.icon} />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.shortcutId && <ShortcutHint shortcutId={item.shortcutId} />}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      <CommandRows
        commands={quickActions}
        heading={text.quickActions}
        onRun={onRunCommand}
      />

      {orgs.length > 0 && (
        <CommandGroup heading={text.organizations}>
          {orgs.map((org) => (
            <CommandItem
              key={String(org._id)}
              value={`org:${String(org._id)}`}
              onSelect={() => onSelectOrg(org)}
            >
              <ItemIcon icon={Building2} />
              <span className="min-w-0 flex-1 truncate">{org.name}</span>
              {org.role && (
                <span className="text-muted-foreground/40 text-xs">
                  {roleLabels[org.role]}
                </span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      <ProductRows
        products={products}
        heading={shouldSearch ? text.products : text.recentProducts}
        formatTime={formatTime}
        onSelect={onSelectProduct}
      />

      <ThreadRows
        threads={threads}
        heading={shouldSearch ? text.threads : text.recentThreads}
        formatTime={formatTime}
        onSelect={onSelectThread}
      />

      <ProjectRows
        projects={projects}
        heading={text.projects}
        formatTime={formatTime}
        onSelect={onSelectProject}
      />

      <AvatarRows
        avatars={avatars}
        heading={text.avatars}
        onSelect={onSelectAvatar}
      />

      <AestheticRows
        aesthetics={aesthetics}
        heading={text.aesthetics}
        onSelect={onSelectAesthetic}
      />

      <MemberRows
        members={members}
        heading={text.members}
        roleLabels={roleLabels}
        onSelect={onSelectMember}
      />
    </>
  );
}
